import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationEllipsis,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/components/utils/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/hooks/useI18n";

interface ListPaginationProps {
  /** Pagina corrente, da 1 */
  page: number;
  pageSize: number;
  /** Righe della lista filtrata, tutte le pagine */
  totalCount: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  /**
   * La lista sta arrivando: al posto dei controlli uno scheletro della stessa altezza,
   * cosi' il footer non sparisce e la pagina non salta (es. al cambio tab).
   */
  loading?: boolean;
}

/** Sotto questa soglia si vedono tutte le pagine, senza puntini. */
const SHOW_ALL_BELOW = 10;

/**
 * Pagine da mostrare, 'gap' dove vanno i puntini. Fino a 9 pagine tutte. Da 10 in su
 * cinque caselle fisse, prima e ultima sempre presenti, cosi' la capsula non cambia
 * larghezza mentre si scorre:
 *
 *   1 2 3 … N        vicino all'inizio
 *   1 … c … N        nel mezzo: solo la corrente, i vicini sono a una freccia
 *   1 … N-2 N-1 N    vicino alla fine
 */
const pageSlots = (page: number, totalPages: number): Array<number | 'gap'> => {
  if (totalPages < SHOW_ALL_BELOW) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (page <= 3) return [1, 2, 3, 'gap', totalPages];
  if (page >= totalPages - 2) return [1, 'gap', totalPages - 2, totalPages - 1, totalPages];
  return [1, 'gap', page, 'gap', totalPages];
};

/**
 * Footer di paginazione unico del planner: lo usa DataTable (client e server side) e le
 * viste a griglia. Sempre visibile quando ci sono righe (anche con una pagina sola),
 * contatore a sinistra e pagine al centro; prev/next si disabilitano agli estremi. Va
 * messo fuori dall'area di scroll, cosi' resta ancorato in fondo.
 */
export const ListPagination = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  disabled = false,
  loading = false,
}: ListPaginationProps) => {
  const { t } = useI18n();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (loading) {
    return (
      <div aria-hidden="true" className="shrink-0 flex flex-col-reverse items-center gap-2 pt-2 sm:relative sm:flex-row sm:justify-center">
        <Skeleton className="h-4 w-32 sm:absolute sm:left-0" />
        <Skeleton className="h-10 w-48 rounded-full" />
      </div>
    );
  }
  if (totalCount <= 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);
  const goTo = (next: number) => {
    if (!disabled && next >= 1 && next <= totalPages && next !== page) onPageChange(next);
  };
  const edgeClasses = (atEdge: boolean) =>
    cn("cursor-pointer", (atEdge || disabled) && "pointer-events-none text-gray-300");

  return (
    // Da telefono il contatore va sotto la capsula: ancorato a sinistra le si sovrapponeva.
    <div className="shrink-0 flex flex-col-reverse items-center gap-2 pt-2 sm:relative sm:flex-row sm:justify-center">
      {/* Da sm in su il contatore sta in `absolute` a sinistra, cosi' non occupa spazio
          nel flow: la paginazione resta centrata sull'intera larghezza. */}
      <div className="text-xs text-gray-500 tabular-nums sm:absolute sm:left-0">
        {t('dashboard.showingResults', { from, to, total: totalCount })}
      </div>
      {/* Su schermi stretti la capsula scorre di lato invece di andare a capo. */}
      <Pagination className="w-auto mx-0 max-w-full">
        {/* Una capsula sola: frecce e numeri sono lo stesso controllo, non pulsanti sparsi. */}
        <PaginationContent className="max-w-full overflow-x-auto gap-0.5 rounded-full border border-gray-200 bg-white p-1 shadow-sm">
          <PaginationItem>
            <PaginationPrevious onClick={() => goTo(page - 1)} className={edgeClasses(page === 1)} />
          </PaginationItem>
          {pageSlots(page, totalPages).map((p, i) => {
            if (p === 'gap') {
              return (
                <PaginationItem key={`gap-${i}`}>
                  <PaginationEllipsis className="h-8 w-6 text-gray-400" />
                </PaginationItem>
              );
            }
            const active = page === p;
            return (
              <PaginationItem key={p}>
                <PaginationLink
                  onClick={() => goTo(p)}
                  isActive={active}
                  className={cn(
                    "cursor-pointer h-8 min-w-8 w-auto px-2 rounded-full border text-xs tabular-nums transition-colors",
                    // Attiva: solo contorno scuro e numero marcato, niente riempimento.
                    active
                      ? "border-gray-800 bg-transparent text-gray-900 font-semibold hover:bg-transparent hover:text-gray-900"
                      : "border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                    disabled && "pointer-events-none"
                  )}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          <PaginationItem>
            <PaginationNext onClick={() => goTo(page + 1)} className={edgeClasses(page === totalPages)} />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
